import * as Sentry from '@sentry/nextjs';
import { reportGuanluError } from './reportGuanluError';

/** 统一 Sentry 上报所需的业务上下文 */
export interface ReportSentryErrorOptions {
  /** 业务域，例如 payment、cart、lens、order */
  domain?: string;
  /** 发生错误的流程阶段，例如 checkout、submit、init */
  stage?: string;
  /** 低基数标签；不要放入订单号、用户 ID 或完整业务对象 */
  tags?: Record<string, string | number | boolean | undefined>;
  /** 诊断信息；发送前会递归脱敏并限制深度和字符串长度 */
  extra?: Record<string, unknown>;
  /** 用于将同一类错误聚合到同一个 Sentry issue，通常不需要指定 */
  fingerprint?: string[];
}

// 防止异常上下文过深或过大，放大请求体、RSC payload 或 Sentry 事件体积。
const MAX_SANITIZE_DEPTH = 3;
const MAX_STRING_LENGTH = 1000;
const REDACTED_VALUE = '[REDACTED]';
const TRUNCATED_VALUE = '[TRUNCATED]';

// 这些字段可能包含凭证、个人信息、请求头或完整业务 payload，禁止原样上报。
const SENSITIVE_KEY_PATTERN =
  /authorization|cookie|password|passwd|pwd|secret|token|email|phone|address|prescription|user_?info|request_?data|payload|headers?/i;

/** 截断过长的诊断字符串，避免错误上下文造成额外性能和存储开销 */
function sanitizeString(value: string) {
  return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value;
}

/**
 * 递归清理可序列化上下文
 * 注意：这里不处理 Error 对象本身；Error 仅作为异常主体交给 Sentry 采集堆栈
 */
function sanitizeValue(value: unknown, depth = 0): unknown {
  if (depth > MAX_SANITIZE_DEPTH) {
    return TRUNCATED_VALUE;
  }

  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, depth + 1));
  }

  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, item]) => {
    result[key] = SENSITIVE_KEY_PATTERN.test(key) ? REDACTED_VALUE : sanitizeValue(item, depth + 1);
    return result;
  }, {});
}

/** 清理 extra/tags，同时保留调用方需要的低基数诊断字段 */
function sanitizeRecord(record?: Record<string, unknown>) {
  if (!record) return undefined;

  return sanitizeValue(record) as Record<string, unknown>;
}

/** 将字符串、普通对象或 null/undefined 异常转换为稳定的 Error 实例 */
function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message) {
      return new Error(message);
    }
  }

  return new Error('Non-Error exception captured by reportSentryError');
}

/**
 * Report a application error to Sentry with consistent metadata and redaction.
 *
 * This entry point is intentionally reserved for errors that block critical
 * business flows, cause fatal runtime failures, or meet the project's P0
 * performance threshold. Callers should pass only low-cardinality tags and
 * diagnostic context. Do not pass complete order, cart, prescription, user,
 * request, or response objects.
 *
 * This module is runtime-neutral: it does not access browser globals or Node-only
 * APIs, so it can be imported by both client components and server code.
 */
export function reportSentryError(error: unknown, options: ReportSentryErrorOptions = {}) {
  const { domain, stage, tags, extra, fingerprint } = options;
  reportGuanluError(options, error);

  // 使用临时 scope，避免本次错误的 tags/extra 污染后续 Sentry 事件
  Sentry.withScope((scope) => {
    if (domain) {
      scope.setTag('error.domain', domain);
    }

    if (stage) {
      scope.setTag('error.stage', stage);
    }

    if (tags) {
      scope.setTags(sanitizeRecord(tags) as Record<string, string | number | boolean>);
    }

    const sanitizedExtra = sanitizeRecord(extra);
    if (sanitizedExtra) {
      scope.setExtras(sanitizedExtra);
    }

    if (fingerprint?.length) {
      scope.setFingerprint(fingerprint.map(sanitizeString));
    }

    Sentry.captureException(normalizeError(error));
  });
}
